import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  useMediaQuery,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
} from "@mui/material";
import TableViewIcon from "@mui/icons-material/TableView";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import regionsData from "@/app/features/company/data/regions_lite.json";
import OpportunityCard from "@/app/global-components/opportunity-card";
import TableComponent from "@/app/global-components/table";
import StatusChip from "@/app/features/tender/components/view/Status";
import { isNull } from "lodash";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { routes } from "@/consts";
import { LANG } from "@/types";
import { SearchIcon, Filter, Plus, TrendingUp } from "lucide-react";
import useOffer from "@/hooks/useTender";

interface CategoryOption {
  value: any;
  label: any;
}
interface Region {
  region_id: number;
  name_ar: string;
}
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
interface Tender {
  id: number;
  title: string;
  value: number;
  region: string;
  city: string;
  type: string;
  status: string;
  endDate: string;
  deliverDate: string;
  categories: string[];
  TenderNr: number;
  category: string;
  location: string;
  remainingTime: number;
  companyId: string;
}

const TenderMinePage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [filterCategories, setFilterCategories] = useState<string[] | string>(
    []
  );
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [regions, setRegions] = useState<CategoryOption[]>([]);
  const [Categoris, setCategoris] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const itemsPerPage = 10;

  const { getMyOffers, getcategories } = useOffer();

  useEffect(() => {
    handleSearch();
  }, [filterCategories]);

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    try {
      const Offers = await getMyOffers(searchInput);
      if (Offers) {
        setTenders(Offers.data);
      } else {
        setTenders([]);
      }
    } catch (error) {
      console.error("Error fetching Offers:", error);
      setError(t("alerts.error.fetching"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRegions(
      regionsData.map((region: Region) => ({
        value: region.region_id.toString(),
        label: region.name_ar,
      }))
    );

    const fetchCategoris = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name)
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item.id,
              label: item.name,
            })
          );
          setCategoris(Cates);
        } else {
          setCategoris([]);
        }
      } catch (error) {
        console.error("Error fetching Offers:", error);
      }
    };

    fetchCategoris();
  }, []);

  const getRegion = (value: string) => {
    const region = regions.find(
      (item) => parseInt(item.value) === parseInt(value)
    );
    return region ? region.label : "";
  };

  const tableColumns = [
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          {t("tenders.requests")}
        </div>
      ),
      field: "TenderNr",
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
          {t("company.fields.address")}
        </div>
      ),
      field: "title",
      render: (row: Tender) => (
        <div className="font-medium text-gray-900 max-w-xs truncate">
          {row.title}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
          {t("tender.value")}
        </div>
      ),
      field: "value",
      render: (row: Tender) => (
        <div className="font-semibold text-purple-700">
          {row.value} {t("currency")}
        </div>
      ),
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
          {t("company.fields.region")}
        </div>
      ),
      field: "region",
      render: (row: Tender) => (
        <div className="text-gray-700">{getRegion(row.region)}</div>
      ),
      hideOnMobile: true,
      sortable: true,
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          {t("orders.fields.status")}
        </div>
      ),
      field: "status",
      render: (row: Tender) => (
        <StatusChip status={isNull(row.status) ? "Planning" : row.status} />
      ),
      sortable: true,
    },
  ];

  return (
    <Box
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        background:
          "linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Header Section */}
      <Box className="flex justify-between items-center">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <Box
              sx={{
                width: 4,
                height: 32,
                background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                borderRadius: 2,
                mr: 2,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: "1.2rem", md: "2rem" },
                fontWeight: 700,
                background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("tenders.manageRequests")}
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#6b7280", ml: 6 }}>
            {t("statistics.quotationsAvailable")}: {tenders.length}
          </Typography>
        </Box>
        <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            onClick={() => navigate(routes.createTender)}
            sx={{
              "&:hover": {
                background: "#18181b",
                color: "white",
              },
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: 1,
              background: "#18181b",
              color: "white",
              p: 2,
              borderRadius: 3,
              transition: "all 0.3s ease",
            }}
          >
            <Plus size={20} />
            {t("actions.createNewTender")}
          </IconButton>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Card
        sx={{ mb: 4, boxShadow: "none", borderRadius: 3, overflow: "hidden" }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                label={t("actions.searching")}
                variant="outlined"
                fullWidth
                dir={locale === LANG.AR ? "rtl" : "ltr"}
                value={searchInput}
                size="small"
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSearch}
                        sx={{ color: "#6b7280" }}
                      >
                        <SearchIcon size={18} />
                      </IconButton>
                    </InputAdornment>
                  ),
                  inputProps: { dir: locale === LANG.AR ? "rtl" : "ltr" },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                    "&:hover": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                      },
                    },
                    "&.Mui-focused": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#644980",
                        borderWidth: 2,
                      },
                    },
                  },
                  "& .MuiInputBase-root": {
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                  },
                  "& .MuiInputLabel-root": {
                    transformOrigin: locale === LANG.AR ? "right" : "left",
                    direction: locale === LANG.AR ? "rtl" : "ltr",
                    ...(locale === LANG.AR
                      ? { right: 30, left: "auto" }
                      : { left: 0, right: "auto" }),
                  },
                  "& legend": {
                    textAlign: locale === LANG.AR ? "end" : "start",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Select
                displayEmpty
                id="categorie-multiple-name"
                input={<OutlinedInput />}
                inputProps={{ "aria-label": "Without label" }}
                multiple
                sx={{
                  minWidth: 300,
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  "& .MuiSelect-select": {
                    paddingRight: "10px !important",
                    paddingLeft: "12px !important",
                    paddingTop: "12px !important",
                    paddingBottom: "12px !important",
                    minHeight: "20px !important",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  },
                  "& .MuiSelect-icon": {
                    left: locale === LANG.EN ? "unset" : 0,
                    marginLeft: 0,
                    right: locale === LANG.EN ? 0 : "unset",
                    color: "#6b7280",
                  },
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                      borderWidth: 2,
                    },
                  },
                  "& .MuiChip-root": {
                    height: 24,
                    fontSize: "0.75rem",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    "& .MuiChip-deleteIcon": {
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      "&:hover": {
                        color: "#ef4444",
                      },
                    },
                  },
                }}
                renderValue={(selected: any) => {
                  if (selected.length === 0) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "#9ca3af",
                        }}
                      >
                        <Filter size={16} />
                        <em>{t("categories.select")}</em>
                      </Box>
                    );
                  }

                  if (selected.length <= 2) {
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value: any) => {
                          const category = Categoris.find(
                            (cat) => cat.value === value
                          );
                          return category ? (
                            <Chip
                              key={value}
                              label={category.label}
                              size="small"
                              sx={{
                                backgroundColor: "#e0e7ff",
                                color: "#3730a3",
                                border: "1px solid #c7d2fe",
                                "& .MuiChip-label": {
                                  padding: "0 8px",
                                },
                              }}
                            />
                          ) : null;
                        })}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        label={`${selected.length} ${t("categories.title")}`}
                        size="small"
                        sx={{
                          backgroundColor: "#644980",
                          color: "white",
                          border: "1px solid #644980",
                          "& .MuiChip-label": {
                            padding: "0 8px",
                          },
                        }}
                      />
                    </Box>
                  );
                }}
                value={filterCategories}
                onChange={(e) => setFilterCategories(e.target.value)}
                MenuProps={{
                  ...MenuProps,
                  PaperProps: {
                    ...MenuProps.PaperProps,
                    sx: {
                      ...MenuProps.PaperProps?.style,
                      borderRadius: 2,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                      "& .MuiMenu-list": {
                        padding: "8px",
                        "&::-webkit-scrollbar": {
                          width: "0px",
                          background: "transparent",
                        },
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                      },
                    },
                  },
                }}
              >
                <MenuItem disabled value="">
                  <em>{t("categories.select")}</em>
                </MenuItem>
                {Categoris.map((name) => (
                  <MenuItem
                    key={name.value}
                    value={name.value}
                    sx={{
                      borderRadius: 1,
                      margin: "2px 0",
                      "&.Mui-selected": {
                        backgroundColor: "#644980",
                        color: "#fff",
                        fontWeight: "600",
                        "&:hover": {
                          backgroundColor: "#644980",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                      "& .MuiCheckbox-root": {
                        color: "inherit",
                        "&.Mui-checked": {
                          color: "inherit",
                        },
                      },
                    }}
                  >
                    <span>{name.label}</span>
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} md={2}>
              <Tooltip title={t("actions.toggleView")} arrow>
                <IconButton
                  onClick={() => setViewMode(!viewMode)}
                  sx={{
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    borderRadius: 2,
                    p: 1.5,
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      color: "#374151",
                    },
                  }}
                >
                  {viewMode ? (
                    <TableViewIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <ViewModuleIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress size={48} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-icon": { fontSize: 24 },
          }}
        >
          {error}
        </Alert>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {!tenders.length ? (
            <Card
              sx={{
                p: 6,
                boxShadow: "none",
                textAlign: "center",
                borderRadius: 3,
              }}
            >
              <Box sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}>
                <TrendingUp size={64} />
              </Box>
              <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
                {t("general.NoDataAvailable")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#9ca3af", mb: 3 }}>
                {t("general.noDataMatchingCriteria")}
              </Typography>
              <IconButton
                onClick={() => navigate(routes.createTender)}
                sx={{
                  background: "#18181b",
                  color: "white",
                  p: 2,
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                }}
              >
                <Plus size={20} />
              </IconButton>
            </Card>
          ) : (
            <>
              {viewMode ? (
                <Grid container spacing={3}>
                  {tenders.map((opportunity) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={opportunity.id}
                    >
                      <OpportunityCard opportunity={opportunity} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                  <TableComponent
                    columns={tableColumns}
                    data={tenders}
                    rowKeyField="id"
                    onRowClick={(row) =>
                      navigate(`${routes.vuetender}/${row.id}`)
                    }
                    pagination={true}
                    rowsPerPage={itemsPerPage}
                    isMobile={isMobile}
                    title={t("tenders.manageRequests")}
                    subtitle={`${tenders.length} ${t("tenders.requests")} ${t(
                      "general.items"
                    )}`}
                    searchable={true}
                    sortable={true}
                    variant="elevated"
                    size="md"
                    emptyStateMessage={t("general.NoDataAvailable")}
                    emptyStateSubmessage={t("general.noDataMatchingCriteria")}
                  />
                </Card>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default TenderMinePage;
